import type Dompurify from 'dompurify'
import {html, render} from 'lit-html'
import {controller, attr} from '@github/catalyst'
import {sendStats} from '@github-ui/stats'
import UsageTracker from './usage-tracker'
import type {UponSanitizeAttributeHookEvent} from 'dompurify'

declare global {
  interface Window {
    MathJax?: MathJaxConfig
  }
}

const MAX_ALLOWED_MACROS = 100
/**
 * While each instance of a math renderer element is limited
 * to 100 macros, there is nothing preventing users with malicious intent
 * from authoring many comments, each with 100 macros. This value allows
 * us to limit the total number of macros on any given page.
 */
const GLOBAL_MAX_ALLOWED_MACROS = 2000

// eslint-disable-next-line i18n-text/no-en
const BAD_EXPRESSION_ERROR = 'Unable to render expression.'
const INLINE_DELIMITER = '$'
const DISPLAY_DELIMITER = '$$'
const MATHJAX_PREFIX_REGEX = new RegExp('^(TEX|mjx|MJX)-')
const DIMENSION_ARG_MACROS = [
  'hskip',
  'hspace',
  'raise',
  'mspace',
  'mskip',
  'kern',
  'lower',
  'above',
  'mkern',
  'moveleft',
  'moveright',
  'rule',
  'Rule',
  'space',
  'Space',
]
const DISALLOWED_MACROS = [
  'DeclareMathOperator',
  'DeclarePairedDelimiters',
  'renewtagform',
  'newtagform',
  'colorbox',
  'fcolorbox',
  'hphantom',
  'vphantom',
  'phantom',
  'operatorname',
  'Newextarrow',
  'definecolor',
  'mathchoice',
  'unicode',
  'mmlToken',
]

// MathML parsing error messages that we want to replace with a generic error message
const MATHML_PARSING_ERROR_MESSAGES = ['Misplaced &']

// Maps possible dimension units that can be passed to LaTeX macros to the maximum value (absolute) that we allow.
const unitToMaxVal: Record<string, number> = {
  em: 2,
  pt: 20,
  pc: 0.75,
  ex: 2.5,
}

const macrosWithDimensionArgs = DIMENSION_ARG_MACROS.join('|')
const unitTypes = Object.keys(unitToMaxVal).join('|')
const dimensionRegex = '-?\\d+.?\\d*?'
const macrosWithDimensionsRegex = new RegExp(`(${macrosWithDimensionArgs})({${dimensionRegex}(${unitTypes})})+`, 'g')

const purifyConfigs = {
  USE_PROFILES: {mathMl: true},
  ADD_ATTR: ['jax', 'unselectable', 'class', 'style', 'size', 'justify', 'space'],
  KEEP_CONTENT: true,
  CUSTOM_ELEMENT_HANDLING: {
    tagNameCheck: MATHJAX_PREFIX_REGEX,
    attributeNameCheck: MATHJAX_PREFIX_REGEX,
    allowCustomizedBuiltInElements: true,
  },
}

const macroUsageTracker = new UsageTracker()

let DOMPurify: null | {default: typeof Dompurify} = null
let configurationPromise: Promise<boolean> | null = null

async function configureMathJax() {
  configurationPromise ||= new Promise(async resolve => {
    window.MathJax = {
      ...(window.MathJax || {}),
      loader: {
        source: {
          'ui/safe': new URL(`mathjax/es5/ui/safe.js`, import.meta.url).toString(),
        },
        load: ['ui/safe'],
      },
      tex: {
        inlineMath: [[INLINE_DELIMITER, INLINE_DELIMITER]],
        displayMath: [[DISPLAY_DELIMITER, DISPLAY_DELIMITER]],
        packages: {'[-]': ['noerrors', 'bbox', 'html', 'require', 'newcommand', 'action', 'colortbl']},
        maxMacros: 1000,
      },
      startup: {
        typeset: false,
        ready() {
          // MathJax has loaded all needed components
          const mathjax = window.MathJax as MathJaxConfig

          // Initializes the MathJax internal objects and creates typesetting and conversion methods
          mathjax.startup.defaultReady!()

          for (const jax of mathjax.startup.document!.inputJax) {
            jax.preFilters.add(async ({math: mathObj}) => {
              for (const macro of DISALLOWED_MACROS) {
                if (mathObj.math.includes(`\\${macro}`)) {
                  mathObj.math = `\\text{${macro} macro is not supported}`
                }
              }

              return true
            }, -1000)
          }

          const safe = mathjax.startup.document?.safe

          if (safe) {
            safe.filterAttributes.set('fontfamily', 'filterSemiColon')
            safe.filterAttributes.set('fontstyle', 'filterSemiColon')
            safe.filterAttributes.set('fontweight', 'filterSemiColon')
            safe.filterMethods.filterSemiColon = function (_unused, input) {
              const [split] = input.split(/;/)
              return split
            }
          }
        },
      },
      options: {
        enableMenu: false,
        safeOptions: {
          allow: {
            URLs: 'none',
            classes: 'none',
            cssIDs: 'none',
            styles: 'safe',
          },
          // Largest padding/border/margin value allowed, in ems
          // Note: There is a bug in mathjax that appears to prevent this setting from working correctly
          lengthMax: 2,
          // an emtpy object here is equivalent to setting all style properties to false, but is required so that lengthMax is respected.
          safeStyles: {},
          styleLengths: {},
        },
      },
    }

    try {
      await import('mathjax/es5/tex-chtml-full')
      await window.MathJax.startup.promise
      DOMPurify = await import('dompurify')
      DOMPurify?.default.addHook('uponSanitizeAttribute', sanitizeClass)
      resolve(true)
    } catch {
      // Catch if import fails
      resolve(false)
    }
  })

  const resolution = await configurationPromise

  // Retries configuring MathJax on soft nav if imports fail
  if (!resolution) {
    configurationPromise = null
  }
}

// Workaround to force DOMPurify to not remove `columnalign` attributes from MathML elements
function allowMathMlAttributes(el: Element, data: UponSanitizeAttributeHookEvent): void {
  const allowedTags = ['mtable']
  const allowedAttributes = ['columnalign']

  if (allowedTags.includes(el.tagName) && allowedAttributes.includes(data.attrName)) {
    data.keepAttr = true
    data.forceKeepAttr = true
  }
}

function sanitizeClass(_: Element, data: UponSanitizeAttributeHookEvent): void {
  // only look at class attributes
  if (data.attrName !== 'class') {
    return
  }

  // remove css classes that don't match the MathJax prefix pattern, `MathJax` or GitHub inline math class
  data.attrValue = data.attrValue
    .split(' ')
    .filter(c => MATHJAX_PREFIX_REGEX.test(c) || c === 'MathJax' || c === 'js-inline-math')
    .join(' ')

  // if all class values are filtered, remove the attribute entirely
  if (data.attrValue === '') {
    data.keepAttr = false
  }
}

@controller
export class MathRendererElement extends HTMLElement {
  #runId!: string

  @attr mathmlEnabled: boolean = false

  /**
   * Create a parallel document node that LaTeX content is injected into.
   * This content will be sanitized and typeset before being re-injected back
   * into the primary document node.
   *
   * We do this to mitigate potentiall XSS and UI redressing attacks that can potentially
   * occur with MathJax's in-place typesetting of LaTeX content.
   **/
  #tempDocumentContentForSanitization() {
    // insert LaTeX into a parallel document to avoid potentially
    // introducing xss vectors
    const doc = document.implementation.createHTMLDocument('')

    doc.open()
    doc.write(`
      <html>
        <body>
          ${this.textContent}
        </body>
      </html>
    `)
    doc.close()

    return doc.body.textContent ?? ''
  }

  #childIsNode(node: Node | null) {
    return Boolean(node && node.nodeType === Node.ELEMENT_NODE)
  }

  #isGitHubError(node: Element | null) {
    return node && node.classList.contains('flash-error')
  }

  #isMathJaxContainer(node: Element | null) {
    // Check whether math being parsed already exists within a MJX-CONTAINER (was already processed by MathJax)
    // or a MATH-RENDERER element (just in case math elements are mistakenly nested somehow)
    return node && (node.nodeName === 'MJX-CONTAINER' || node.nodeName === 'MATH-RENDERER')
  }

  #wasPreviouslyRendered() {
    const node = this.firstElementChild
    return this.#isGitHubError(node) || this.#isMathJaxContainer(node)
  }

  #getMathJaxError(node: HTMLElement) {
    if (!node) {
      return null
    }

    const selector = 'merror'
    const errorNode = node.querySelector(selector)
    let errorMessage = errorNode?.textContent ?? null

    if (errorMessage) {
      // Replace MathML's weird parsing error messages with a generic error message
      const containsParsingError = MATHML_PARSING_ERROR_MESSAGES.some(
        (mjxErrorMessage: string) => errorMessage?.includes(mjxErrorMessage),
      )

      if (containsParsingError) {
        errorMessage = BAD_EXPRESSION_ERROR
      }
    }

    return errorMessage
  }

  #renderError({msg, raw}: {msg?: string | null; raw?: string} = {}) {
    const errorMsg = msg ?? BAD_EXPRESSION_ERROR
    const rawContent = raw ?? this.textContent

    if (!rawContent) {
      return render(html`<div class="flash flash-error">${errorMsg}</div>`, this)
    }

    return render(
      html`<div class="flash flash-error">${errorMsg}</div>
        <br />
        <pre>${rawContent}</pre>`,
      this,
    )
  }

  // certain macros allow the user to alter other parts of the page, such as by
  // allowing custom operators to override existing ones.  We disallow these.
  #checkDisallowedMacros() {
    const containsMacroLiteral = (macro: string) => {
      // Check for a literal instance of a macro, e.g. \DeclareMathOperator
      return this.textContent.includes(`\\${macro}`)
    }

    const containsCloakedMacro = (macro: string) => {
      // Check for a macro preceded by a $, e.g. \$DeclareMathOperator
      // Renders to the macro literal in the context of some other macros, e.g. \ce
      return this.textContent.includes(`\\$${macro}`)
    }

    const containsBrokenUpMacro = (macro: string) => {
      // Check for macros with arguments broken up, e.g. \${DeclareMathOperator{...}{...}}$
      // This pattern can be used to obfuscate the macro
      return this.textContent.includes(`\\$\{${macro}`)
    }

    const disallowedMacrosFound = []
    for (const macro of DISALLOWED_MACROS) {
      if (containsMacroLiteral(macro) || containsCloakedMacro(macro) || containsBrokenUpMacro(macro)) {
        disallowedMacrosFound.push(macro)
      }
    }
    return disallowedMacrosFound
  }

  // Effectively does what the lengthMax attribute of safeOptions is supposed to but evidently does not;
  // limits the value of numeric arguments to macros that can change padding or spacing, such as \hskip or \raise
  // such that users cannot alter GitHub's UI.
  #enforcePaddingLimit(textContent: string) {
    // match all macros that can change padding or spacing (listed in DIMENSION_ARG_MACROS)
    return textContent.replace(macrosWithDimensionsRegex, (match, other) => {
      const dimensionAndUnitRegex = new RegExp(`(${dimensionRegex}(${unitTypes}))+`, 'g')
      const matches = match?.match(dimensionAndUnitRegex)
      if (!matches || !matches.length) {
        return match
      }
      // iterate through each dimension argument and clamp its value according to the allowed max value for its unit.
      const output = matches.map(m => {
        const unit = m.split(new RegExp(`(${unitTypes})`))

        if (!unit.length) {
          return m
        }

        const numericArg = Number(unit[0])
        const unitType = unit[1] ?? ''

        if (!unitType) {
          return m
        }

        const maxVal = unitToMaxVal[unitType] ?? 0
        if (numericArg < -maxVal || numericArg > maxVal) {
          return `{${maxVal}${unitType}}`
        } else {
          return `{${m}}`
        }
      })
      return `${other}${output.join('')}`
    })
  }

  #enforceMathJaxSafety() {
    if (!this.textContent) {
      return ''
    }

    return this.#enforcePaddingLimit(this.textContent)
    // TODO: Call a method here that disallows passing `transparent` as a color argument
    // and potentially other methods that enforce UI safeguards
  }

  // Workaround for WebKit bug (https://bugs.webkit.org/show_bug.cgi?id=160075)
  // that prevents columnalign from being applied to mtd elements
  #setColumnAlignments() {
    const validAlignments = ['left', 'center', 'right']
    const tables = this.querySelectorAll('mtable[columnalign]')

    for (const table of tables) {
      const alignments = table.getAttribute('columnalign')?.trim().split(/\s+/)

      if (alignments && alignments.length > 1) {
        const rows = table.querySelectorAll(':scope > mtr')

        for (const row of rows) {
          const columns = row.querySelectorAll('mtd')

          for (let j = 0; j < columns.length; j++) {
            const column = columns[j]
            const alignment = alignments[j] ?? alignments[alignments.length - 1]

            if (column?.style && alignment && validAlignments.includes(alignment)) {
              column.style.textAlign = alignment
            }
          }
        }
      }
    }
  }

  #renderContent(node: Node) {
    // Determine if typesetting in the parallel document produced an error.
    // Do this first so we can skip a potentially unecessary sanitization pass.
    const errorText = this.#getMathJaxError(node as HTMLElement)

    if (typeof errorText === 'string') {
      this.#renderError({
        msg: errorText,
      })

      return
    }

    /**
     * If the supplied node doesn't have a mathjax error, that doesn't mean
     * it is a valid node we can display.
     * Nodes that have a text node as the value of their `firstChild` property
     * also indicates a math expression that has failed to parse correctly
     */
    if (!this.#childIsNode(node)) {
      this.#renderError()
      return
    }

    const sanitized = DOMPurify?.default.sanitize(node, purifyConfigs) as string

    // Only reset nodes if content remains post-sanitization
    if (!sanitized) {
      this.#renderError()
      return
    }

    this.innerHTML = sanitized
  }

  get isInline(): boolean {
    return this.classList.contains('js-inline-math')
  }

  get isDisplay(): boolean {
    return this.classList.contains('js-display-math')
  }

  override get textContent() {
    return this.firstElementChild?.textContent ?? super.textContent ?? ''
  }

  override set textContent(content: string) {
    if (this.firstElementChild) {
      this.firstElementChild.textContent = content
    } else {
      super.textContent = content
    }
  }

  async connectedCallback() {
    this.#runId = this.getAttribute('data-run-id')!

    macroUsageTracker.update(this.#runId)

    await configureMathJax()

    requestAnimationFrame(() => {
      this.renderMath()
    })
  }

  convertTexToMathML() {
    const parallelDocContent = this.#tempDocumentContentForSanitization()
    const mathJax = window.MathJax

    // We need to remove the $ delimiters from the TeX content before feeding it to MathJax
    const tex = parallelDocContent
      .trim()
      .replace(new RegExp(`^\\${INLINE_DELIMITER}{1,2}|\\${INLINE_DELIMITER}{1,2}$`, 'g'), '')

    if (mathJax && mathJax.tex2mml) {
      const mathMl = mathJax.tex2mml(tex, {display: !this.isInline})

      /**
       * Add a custom hook to preserve table attributes,
       * which should have been preserved with DOMPurify's "ALLOWED_ATTR" and "ADD_ATTR" configs,
       * but that apparently isn't working at this time.
       */
      DOMPurify?.default.addHook('uponSanitizeAttribute', allowMathMlAttributes)
      const sanitizedDocBody = DOMPurify?.default.sanitize(mathMl, {
        USE_PROFILES: {mathMl: true},
        RETURN_DOM: true,
      }) as Element

      this.#renderContent(sanitizedDocBody.firstElementChild as Node)

      // Fix any misaligned math tables in browsers that don't yet support `mtable[columnalign]`
      this.#setColumnAlignments()
    }
  }

  disconnectedCallback() {
    macroUsageTracker.clear(this.#runId)
  }

  renderMath() {
    if (!window.MathJax) {
      return
    }

    // If the document has already been processed, this function is a no-op.
    // This can happen when e.g. a user navigates between files with mathjax in them using
    // the browser's back button.
    if (this.#wasPreviouslyRendered()) {
      return
    }

    // Large amounts of macros can crash the page, so we set an upper limit
    const estimatedElMacroCount = this.textContent.split('{').length
    macroUsageTracker.update(this.#runId, estimatedElMacroCount)

    const aggregateMacroCount = macroUsageTracker.total()

    if (aggregateMacroCount > GLOBAL_MAX_ALLOWED_MACROS || estimatedElMacroCount > MAX_ALLOWED_MACROS) {
      this.#renderError()
      return
    }

    // MathJax allows some unsafe macros, such as \DeclareMathOperator, that can be used to
    // override existing operators or create new ones. This can be leveraged to alter the UI of the page
    // or introduce other unsafe behavior.
    // If such a macro is found, we render an error message and do not attempt to render the math.
    const disallowedMacrosFound = this.#checkDisallowedMacros()
    if (disallowedMacrosFound.length > 0) {
      this.#renderError({
        msg: `The following macros are not allowed: ${disallowedMacrosFound.join(', ')}`,
      })
      return
    }

    this.textContent = this.#enforceMathJaxSafety()

    const startTime = window.performance.now()

    this.convertTexToMathML()
    this.recordBrowserStats(startTime)
  }

  recordBrowserStats(startTime: DOMHighResTimeStamp) {
    // Send render timing distribution stats to Datadog
    const endTime = window.performance.now()
    const timeDiff = endTime - startTime

    const stat: PlatformBrowserStat = {
      distributionValue: timeDiff,
      requestUrl: window.location.href,
    }

    if (this.isInline) {
      stat['distributionKey'] = 'MATH_INLINE_RENDERED'
    } else if (this.isDisplay) {
      stat['distributionKey'] = 'MATH_DISPLAY_RENDERED'
    } else {
      return
    }

    sendStats(stat)
    sendStats({incrementKey: 'MATHML_RENDERED'})
  }
}
